"use client";
import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { Cpu, HelpCircle, Layers, ShieldCheck } from "lucide-react";
import { KineticPageShell } from "@/components/kinetic/KineticPageShell";
import { KineticWorkshopFooter } from "@/components/kinetic/KineticWorkshopFooter";

export default function ArticlePage() {
  const [activeTab, setActiveTab] = useState<"technical" | "eli5">("technical");

  return (
    <KineticPageShell className="relative overflow-hidden">
      {/* Structural HUD grid lines */}
      <div
        className="border-kinetic-on-surface/5 pointer-events-none fixed right-0 top-0 z-0 h-screen w-1/4 border-l"
        aria-hidden
      />
      <div
        className="border-kinetic-on-surface/5 pointer-events-none fixed left-0 top-1/2 z-0 h-px w-screen border-t"
        aria-hidden
      />

      {/* Kinetic ambient glow blurs */}
      <div className="absolute top-[8%] right-[-15%] w-[500px] h-[500px] rounded-full bg-kinetic-primary-container/10 blur-[130px]" />
      <div className="absolute bottom-[25%] left-[-10%] w-[600px] h-[600px] rounded-full bg-kinetic-secondary-fixed/5 blur-[150px]" />

      <main className="relative z-10 min-h-screen pb-16 pt-28 md:pb-24 md:pt-32">
        <div className="mx-auto max-w-5xl px-4 sm:px-8">
          
          <header className="relative mb-12 pl-5 md:mb-20 md:pl-0">
            <div
              className="absolute left-0 top-0 h-full w-1 bg-kinetic-primary-container md:-left-4"
              aria-hidden
            />
            <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
              <div>
                <div className="mb-2 font-mono text-xs tracking-tighter text-kinetic-secondary-fixed">
                  PROTOCOL_ID: RLM_CORE_INGESTION_V1
                </div>
                <h1 className="font-headline text-4xl font-extrabold uppercase leading-none tracking-tighter text-white sm:text-5xl md:text-6xl">
                  Self-Governing Codebases
                </h1>
              </div>
              <div className="text-left md:text-right font-mono text-[10px] uppercase tracking-widest text-kinetic-outline">
                <div>AUTHOR: Peter Hanssens</div>
                <div className="text-kinetic-primary-container font-bold mt-1">2 June 2026</div>
              </div>
            </div>
          </header>

          {/* Intro Section */}
          <div className="kinetic-glass-panel border-kinetic-on-surface/10 relative border-l p-6 md:p-8 mb-12">
            <div className="text-kinetic-primary-fixed/30 absolute right-0 top-0 p-2 font-mono text-[8px] tracking-tighter">
              INGESTION_ROUTING: ACTIVE
            </div>
            <p className="text-base font-medium leading-relaxed text-kinetic-on-surface-variant md:text-lg">
              In modern engineering, documentation is a promise that is rarely kept. Architectural models and wiki pages look pristine on Day One, but the moment developers merge the first pull request, physical code paths shift, signatures change, and the map decays. Here is how we solved document drift by engineering a self-documenting system driven by **Recursive Language Models (RLMs)**.
            </p>
          </div>

          {/* Core Pillars Section */}
          <div className="mb-16">
            <div className="relative pl-5 md:pl-0 mb-8">
              <div
                className="absolute left-0 top-0 h-full w-0.5 bg-kinetic-secondary-fixed md:-left-3"
                aria-hidden
              />
              <div className="font-mono text-xs tracking-tighter text-kinetic-secondary-fixed uppercase">
                SYSTEM_PILLARS // CONCEPTUAL_MODEL
              </div>
              <h2 className="font-headline text-2xl font-bold uppercase tracking-tight text-white mt-1">
                The Three Pillars of Self-Governance
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Pillar 1: Ontology */}
              <div className="relative border border-kinetic-on-surface/5 bg-kinetic-surface-container-lowest/40 hover:bg-kinetic-surface-container-lowest/80 p-6 transition-all duration-300 group">
                <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-cyan-500/0 via-cyan-500/40 to-cyan-500/0 opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-2 border border-cyan-500/20 bg-cyan-950/20 text-cyan-400">
                    <Layers className="h-5 w-5" />
                  </div>
                  <div>
                    <span className="font-mono text-[9px] text-cyan-400/60 block tracking-widest">PILLAR_01</span>
                    <h3 className="font-headline text-sm font-bold text-white uppercase tracking-wider">
                      The Ontology
                    </h3>
                  </div>
                </div>
                <p className="text-xs leading-relaxed text-kinetic-on-surface-variant">
                  A strongly-typed, formal model representing the codebase's entities, properties, and relationships. It enforces semantic rules (e.g. which files implement which concepts), serving as the <span className="text-white font-semibold">authoritative ruleset</span> of your software architecture.
                </p>
                <div className="mt-4 font-mono text-[10px] text-cyan-400">
                  // Rules & Architecture Map
                </div>
              </div>

              {/* Pillar 2: RLM */}
              <div className="relative border border-kinetic-on-surface/5 bg-kinetic-surface-container-lowest/40 hover:bg-kinetic-surface-container-lowest/80 p-6 transition-all duration-300 group">
                <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-kinetic-primary-container/0 via-kinetic-primary-container/40 to-kinetic-primary-container/0 opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-2 border border-kinetic-primary-container/20 bg-kinetic-primary-container/10 text-kinetic-primary-container">
                    <Cpu className="h-5 w-5" />
                  </div>
                  <div>
                    <span className="font-mono text-[9px] text-kinetic-primary-container/60 block tracking-widest">PILLAR_02</span>
                    <h3 className="font-headline text-sm font-bold text-white uppercase tracking-wider">
                      The RLM
                    </h3>
                  </div>
                </div>
                <p className="text-xs leading-relaxed text-kinetic-on-surface-variant">
                  An autonomous, recursive agent loop executing dynamically inside a sandboxed VM. Instead of making blind, one-shot predictions, the RLM writes modular inspection scripts to <span className="text-white font-semibold">explore files and auto-correct errors</span> based on compiler feedback.
                </p>
                <div className="mt-4 font-mono text-[10px] text-kinetic-primary-container">
                  // Autonomous Inspection Brain
                </div>
              </div>

              {/* Pillar 3: Combined Synergy */}
              <div className="relative border border-kinetic-on-surface/5 bg-kinetic-surface-container-lowest/40 hover:bg-kinetic-surface-container-lowest/80 p-6 transition-all duration-300 group md:col-span-1">
                <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-emerald-500/0 via-emerald-500/40 to-emerald-500/0 opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-2 border border-emerald-500/20 bg-emerald-950/20 text-emerald-400">
                    <ShieldCheck className="h-5 w-5" />
                  </div>
                  <div>
                    <span className="font-mono text-[9px] text-emerald-400/60 block tracking-widest">SYNERGY</span>
                    <h3 className="font-headline text-sm font-bold text-white uppercase tracking-wider">
                      Combined Synergy
                    </h3>
                  </div>
                </div>
                <p className="text-xs leading-relaxed text-kinetic-on-surface-variant">
                  Ontology feeds the RLM its structural reality, and the RLM acts as the automated curator. Validations check code against active policies, and local, <span className="text-white font-semibold">zero-token AST sync hooks</span> instantly record file line movements at zero cost.
                </p>
                <div className="mt-4 font-mono text-[10px] text-emerald-400">
                  // Self-Governing Codebase
                </div>
              </div>
            </div>
          </div>

          {/* Tab Selector Section */}
          <div className="mb-12">
            <div className="flex space-x-2 font-mono text-[9px] uppercase tracking-wider mb-8">
              {[
                { id: "technical", label: "01_TECHNICAL_DEEP_DIVE", icon: Cpu },
                { id: "eli5", label: "02_EXPLAIN_IT_LIKE_IM_5", icon: HelpCircle },
              ].map(item => (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id as any)}
                  className={`border px-4 py-3 transition-colors flex items-center space-x-2 ${
                    activeTab === item.id
                      ? "border-kinetic-primary-container text-kinetic-primary-container bg-kinetic-surface-container-lowest"
                      : "border-kinetic-outline-variant/40 text-kinetic-on-surface-variant hover:border-kinetic-primary-container"
                  }`}
                >
                  <item.icon className="h-3 w-3 shrink-0" />
                  <span>{item.label.replace(/_/g, ' ')}</span>
                </button>
              ))}
            </div>

            {/* Content card */}
            <div className="border border-kinetic-on-surface/5 bg-kinetic-surface-container-lowest p-6 md:p-8 relative">
              <div className="absolute -top-3 left-4 bg-kinetic-surface px-2 font-mono text-[10px] text-kinetic-primary-container">
                EXPLAINER: {activeTab.toUpperCase()}_INGRESS
              </div>

              {activeTab === "technical" ? (
                <div className="space-y-6">
                  <div>
                    <h3 className="font-headline text-lg font-bold text-white uppercase tracking-wider mb-3">
                      The Power of Recursive Language Models (RLMs)
                    </h3>
                    <p className="text-sm leading-relaxed text-kinetic-on-surface-variant">
                      Traditional codebase ingestion strategies scale poorly. Ingesting an entire repository of raw source files into an LLM window quickly exhausts context budgets, incurs high API costs, and limits structural reasoning.
                    </p>
                    <p className="text-sm leading-relaxed text-kinetic-on-surface-variant mt-3">
                      A <span className="text-white font-bold">Recursive Language Model (RLM)</span> changes this paradigm by running a closed execution loop inside an isolated environment. Instead of predicting everything in a single, blind shot, the RLM harness executes bare Go statements dynamically inside a sandboxed interpreter (Yaegi). It uses system code-analysis helpers (like <code>ListFiles()</code>, <code>ParseFileSymbols()</code>, and <code>FindReferences()</code>) to investigate the filesystem iteratively and construct its database step-by-step.
                    </p>
                  </div>

                  <div className="border border-kinetic-primary-fixed/20 bg-kinetic-primary-container/5 p-4 rounded-xl">
                    <div className="font-mono text-[9px] text-kinetic-primary-container uppercase tracking-widest mb-2 flex items-center space-x-1">
                      <Layers className="h-3 w-3" />
                      <span>Closed Ingestion Execution Loop:</span>
                    </div>
                    <p className="text-xs text-kinetic-on-surface-variant leading-relaxed">
                      The orchestrator calls recursive sub-queries using <code>Query()</code> for complex tasks, executes edits, matches schemas, runs validations (<code>DroverFsck()</code>), and terminates cleanly only when the validation returns zero violations.
                    </p>
                  </div>

                  <div>
                    <h3 className="font-headline text-lg font-bold text-white uppercase tracking-wider mb-3">
                      🔒 Secure Sandboxing & Execution Safety
                    </h3>
                    <p className="text-sm leading-relaxed text-kinetic-on-surface-variant">
                      Executing code generated dynamically by an AI can introduce host vulnerabilities. To make this production-ready, we implemented a layered execution sandbox:
                    </p>
                    <ul className="text-xs text-kinetic-outline-variant space-y-3 pl-4 list-disc mt-3">
                      <li>
                        <strong className="text-white">Unsafe Package Stripping:</strong> Standard packages that allow direct access to disk or host environments (<code>os</code>, <code>os/exec</code>, <code>syscall</code>, and <code>net/*</code> network calls) are completely removed from the Yaegi standard catalog. The AI can only call sandboxed data mutation helpers.
                      </li>
                      <li>
                        <strong className="text-white">Deterministic Timeouts:</strong> Every VM call and query is capped via strict timeouts to guarantee the execution loop never hangs or blocks indefinitely.
                      </li>
                      <li>
                        <strong className="text-white">Delta Ingestion Mode:</strong> In Delta Mode, the system runs a local <code>git status --porcelain</code> scanner, loading *only* modified or newly introduced file comparison blocks. This reduces prompt footprints by **99%** (down to 61 KB) and saves massive API costs.
                      </li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="font-headline text-lg font-bold text-white uppercase tracking-wider mb-3">
                      💰 Zero-Token AST Synchronization
                    </h3>
                    <p className="text-sm leading-relaxed text-kinetic-on-surface-variant">
                      AI calls are expensive. We don't query an LLM when developer code simply shifts around. We engineered a lightweight, local JS syncing utility (<code>sync-ast-lineages.js</code>) and a Git pre-commit hook that parses symbols locally, matches modified signatures, and automatically synchronizes codebase lineages inside <code>graph.jsonl</code> in under 0.2 seconds—costing absolutely nothing.
                    </p>
                  </div>

                  <div>
                    <h3 className="font-headline text-lg font-bold text-white uppercase tracking-wider mb-3">
                      🤖 Continuous Integration Policy Gates
                    </h3>
                    <p className="text-sm leading-relaxed text-kinetic-on-surface-variant">
                      Documentation is only valuable if it is strictly enforced. We built a reusable composite GitHub Action CI check. Every time a developer opens a Pull Request, the validator tests the graph constraints:
                    </p>
                    <ul className="text-xs text-kinetic-outline-variant space-y-2 pl-4 list-disc mt-3">
                      <li>Are all new <code className="font-mono text-white text-[10px]">Term</code> objects governed by a curation role (<code className="font-mono text-white text-[10px]">governed_by</code>)?</li>
                      <li>Does the taxonomy hierarchy remain acyclic (<code className="font-mono text-white text-[10px]">broader_term</code>)?</li>
                      <li>Do approved plans have the required reviews mapped in the SQLite projection?</li>
                    </ul>
                    <p className="text-sm leading-relaxed text-kinetic-on-surface-variant mt-3">
                      If any constraint is violated, the check fails, PR annotations are raised, and merging is blocked—preventing architectural decay before code ever reaches production.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  <div>
                    <h3 className="font-headline text-lg font-bold text-white uppercase tracking-wider mb- flex items-center space-x-2">
                      <span>🤖</span>
                      <span>Meet the Little Robot Architect!</span>
                    </h3>
                    <p className="text-sm leading-relaxed text-kinetic-on-surface-variant mt-3">
                      Imagine you have a giant, messy bucket of LEGO bricks, and you want to build a magnificent castle.
                    </p>
                  </div>

                  <div className="border border-rose-500/20 bg-rose-950/10 p-5 rounded-xl">
                    <h4 className="font-mono text-xs font-bold text-rose-400 uppercase tracking-widest mb-2">❌ The Traditional Way</h4>
                    <p className="text-xs text-kinetic-on-surface-variant leading-relaxed">
                      A regular AI is like a builder who stands far away across the room. They look at the messy bucket once, close their eyes, and try to draw a perfect blueprint of the castle from memory. Usually, they get some things wrong because they can't actually touch the bricks, see if they fit, or fix mistakes.
                    </p>
                  </div>

                  <div className="border border-emerald-500/20 bg-emerald-950/10 p-5 rounded-xl">
                    <h4 className="font-mono text-xs font-bold text-emerald-400 uppercase tracking-widest mb-2">✅ The RLM Way (The Smart Little Robot)</h4>
                    <p className="text-xs text-kinetic-on-surface-variant leading-relaxed">
                      A <span className="font-bold text-white">Recursive Language Model (RLM)</span> is like a smart little robot builder who jumps right into the bucket of bricks! The robot picks up a piece, tests if it snaps cleanly to another, and checks if it makes the wall stronger. If a piece doesn't fit, the robot doesn't panic—it throws it back, grabs a better brick, and keeps building piece-by-piece until it has built a perfect, sturdy castle.
                    </p>
                  </div>

                  <div>
                    <h3 className="font-headline text-lg font-bold text-white uppercase tracking-wider mb-3">
                      Why is this super cool for developers?
                    </h3>
                    <p className="text-sm leading-relaxed text-kinetic-on-surface-variant">
                      As developers, we are constantly moving, renaming, and changing our code (like moving our LEGO walls around). Normally, we'd have to sit down and rewrite boring instruction manuals and draw new pictures.
                    </p>
                    <p className="text-sm leading-relaxed text-kinetic-on-surface-variant mt-3">
                      With Drover and our little robot, the moment we save our changes, a tiny local inspector checks our work, automatically updates the maps, and alerts us if we broke any rules (like building a wall without any pillars). 
                    </p>
                    <p className="text-sm leading-relaxed text-kinetic-on-surface-variant mt-3">
                      Our diagrams are <span className="text-white font-bold">never stale</span>, our code is <span className="text-white font-bold">always safe</span>, and we can focus on building awesome things!
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Action call to action */}
          <div className="border-kinetic-error/20 bg-kinetic-error-container/5 relative mt-16 border-2 border-dashed p-8 text-center max-w-3xl mx-auto">
            <div className="absolute -top-3 left-4 bg-kinetic-surface px-2 font-mono text-[10px] text-kinetic-primary-fixed">
              SYSTEM_BOOTSTRAP_ACTION
            </div>
            <h3 className="font-headline text-xl font-bold uppercase tracking-tight text-white mb-3">
              Deploy Governed Ingestion Loops
            </h3>
            <p className="text-sm leading-relaxed text-kinetic-on-surface-variant max-w-xl mx-auto mb-6">
              Ready to eliminate codebase drift and enforce architectural policies at scale? Deploy the local visualizer and deep-link your design models directly into VS Code or Cursor natively.
            </p>
            <Link
              href="/contact-us"
              className="hover:bg-kinetic-primary-fixed/5 relative inline-block border border-kinetic-primary-fixed px-8 py-3 font-headline text-xs font-bold uppercase tracking-widest text-kinetic-primary-fixed transition-all"
            >
              <span
                className="absolute -left-1 -top-1 h-2 w-2 border-l-2 border-t-2 border-kinetic-primary-fixed"
                aria-hidden
              />
              <span
                className="absolute -bottom-1 -right-1 h-2 w-2 border-b-2 border-r-2 border-kinetic-primary-fixed"
                aria-hidden
              />
              BOOK_FREE_CONSULTATION
            </Link>
          </div>

        </div>
      </main>

      <div className="relative z-10">
        <KineticWorkshopFooter />
      </div>
    </KineticPageShell>
  );
}
